import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SubscriptionService } from '@app/shared/services/subscription.service';
import { GeoSnackBarService } from '@app/shared/services/snack-bar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, finalize, tap } from 'rxjs';

export interface UpgradeConfirmationData {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  amount?: number;
  currency?: string;
}

@Component({
  selector: 'app-upgrade-confirmation-dialog',
  templateUrl: './upgrade-confirmation-dialog.component.html',
  styleUrls: ['./upgrade-confirmation-dialog.component.scss'],
})
export class UpgradeConfirmationDialogComponent {
  promotionCode = '';
  isPromoApplied = false;
  isValidatingPromo = false;
  discountPercentage: number | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: UpgradeConfirmationData,
    public matDialogRef: MatDialogRef<UpgradeConfirmationDialogComponent>,
    private subscriptionService: SubscriptionService,
    private snackBarService: GeoSnackBarService,
    private translateService: TranslateService
  ) { }


  get discountAmount(): number {
    if (this.isPromoApplied && this.discountPercentage && this.data.amount) {
      return this.data.amount * (this.discountPercentage / 100);
    }
    return 0;
  }

  get finalAmount(): number {
    if (this.data.amount) {
      return this.data.amount - this.discountAmount;
    }
    return 0;
  }

  onApplyPromo(): void {
    if (!this.promotionCode?.trim()) {
      return;
    }
    this.isValidatingPromo = true;
    this.subscriptionService
      .validatePromotionCode(this.promotionCode.trim())
      .pipe(
        tap((res) => {
          if (
            res?.data?.valid &&
            typeof res.data.discount_percentage === 'number'
          ) {
            this.isPromoApplied = true;
            this.discountPercentage = res.data.discount_percentage;
            this.snackBarService.success({
              title: this.translateService.instant('COMPANY.PROMO_APPLIED'),
            });
          } else {
            this.isPromoApplied = false;
            this.discountPercentage = null;
            this.snackBarService.error({
              title: this.translateService.instant('GENERAL.ERROR'),
              message: this.translateService.instant('GENERAL.INVALID_PROMO'),
              dismissible: true,
            });
          }
        }),
        catchError(() => {
          this.isValidatingPromo = false;
          this.isPromoApplied = false;
          this.discountPercentage = null;
          this.snackBarService.error({
            title: this.translateService.instant('GENERAL.ERROR'),
            message: this.translateService.instant('GENERAL.INVALID_PROMO'),
            dismissible: true,
          });
          return EMPTY;
        }),
        finalize(() => (this.isValidatingPromo = false))
      )
      .subscribe();
  }

  onPromoCodeChange(value: string): void {
    this.promotionCode = value;
    if (!value?.trim()) {
      this.isPromoApplied = false;
      this.discountPercentage = null;
    }
  }

  onConfirm(): void {
    this.matDialogRef.close({
      confirmed: true,
      promotionCode: this.isPromoApplied ? this.promotionCode : undefined,
    });
  }

  onCancel(): void {
    this.matDialogRef.close({ confirmed: false });
  }
}

